// =====================================================
// utils/torneos.ts
// Utilidades para Torneos - Multi-Deporte
// =====================================================

import { calcularEstadisticas } from './resultados';

export type Deporte = "Tenis" | "Padel" | "Futbol";

// ---------------------------------------------------------
// Utilidades generales
// ---------------------------------------------------------
export function nextPowerOf2(n: number): number {
  return Math.pow(2, Math.ceil(Math.log2(Math.max(1, n))));
}

// ---------------------------------------------------------
// Interfaz de Partido
// ---------------------------------------------------------
export interface Partido {
  id: number;
  ronda: number;
  local: string | Equipo | null;
  visitante: string | Equipo | null;
  pasaAutomatico: boolean;
  ganador?: string | Equipo | null;
  dePartido?: [number, number];
  detalles?: DetallesPartido;
  sets?: Array<{ gamesLocal: number | null; gamesVisitante: number | null }>;
}

export interface Equipo {
  id: number;
  nombre: string;
  ids?: {
    idJugador1: number;
    idJugador2?: number;
  };
}

export interface DetallesPartido {
  fecha?: string;
  hora?: string;
  lugar?: string;
  observaciones?: string;
}

// ---------------------------------------------------------
// Interfaz de Ronda
// ---------------------------------------------------------
export interface Ronda {
  numero: number;
  partidos: Partido[];
}

export type EstrategiaByes = "orden_zonas" | "mejor_clasificacion";
export type ModoZonas = "flexible" | "apa";

// ---------------------------------------------------------
// Tipos para Modo Cruces de Zona
// ---------------------------------------------------------
export type TipoCruce = 'inicial_1' | 'inicial_2' | 'final' | 'tercer_puesto';
export type ModalidadCruce = 'todos_contra_todos' | 'cruces';

export interface PartidoZona {
  id: string | number;
  local: Equipo;
  visitante: Equipo;
  sets: Array<{ gamesLocal: number | null; gamesVisitante: number | null }>;
  jugado: boolean;
  detalles: DetallesPartido;
  ganador?: Equipo | null;
  resultado?: string;
  tipoCruce?: TipoCruce;
}

export interface ZonaCruces {
  id: number;
  nombre?: string;
  equipos: Equipo[];
  partidos: PartidoZona[];
  modalidadCruce?: ModalidadCruce;
}

export interface FilaTabla {
  equipo: Equipo;
  pj: number;
  pg: number;
  pe: number;
  pp: number;
  setsGanados: number;
  setsPerdidos: number;
  setsDiff: number;
  gamesGanados: number;
  gamesPerdidos: number;
  gamesDiff: number;
  puntos: number;
  posicionCruce?: number;
}

// ---------------------------------------------------------
// Interfaz de Resultado de Torneo
// ---------------------------------------------------------
export interface ResultadoTorneo {
  formato: "eliminacion_directa" | "zonas_y_eliminacion";
  total: number;
  cuadroFinal: number;
  byes: number;
  fases: number;
  rondas: Ronda[];
  zonas?: number;
  participantesPorZona?: number[];
  clasificadosPorZona?: number[];
  totalClasificados?: number;
  criterioClasificacion?: string;
  estrategiaByes?: EstrategiaByes;
  modoZonas?: ModoZonas;
  zonasDeseadas?: number;
  deporte?: Deporte;
}

// ---------------------------------------------------------
// NUEVA: Crear sets iniciales según deporte
// ---------------------------------------------------------
function crearSetsIniciales(deporte: Deporte): Array<{ gamesLocal: number | null; gamesVisitante: number | null }> {
  if (deporte === 'Futbol') {
    return [{ gamesLocal: null, gamesVisitante: null }];
  } else {
    return [
      { gamesLocal: null, gamesVisitante: null },
      { gamesLocal: null, gamesVisitante: null },
    ];
  }
}

// ---------------------------------------------------------
// Generar partidos de zona
// ---------------------------------------------------------
export function generarPartidosDeZona(
  equipos: any[],
  deporte: Deporte = 'Tenis'
) {
  const partidos = [];
  let partidoId = 1;

  for (let i = 0; i < equipos.length; i++) {
    for (let j = i + 1; j < equipos.length; j++) {
      partidos.push({
        id: partidoId++,
        local: equipos[i],
        visitante: equipos[j],
        sets: crearSetsIniciales(deporte),
        jugado: false,
        detalles: {}
      });
    }
  }

  return partidos;
}

// ---------------------------------------------------------
// Calcular tabla de zona
// ---------------------------------------------------------
export function calcularTablaZona(zona: any, deporte: Deporte = 'Tenis') {
  const tabla = zona.equipos.map((e: any) => ({
    equipo: e,
    pj: 0,
    pg: 0,
    pe: 0,
    pp: 0,
    setsGanados: 0,
    setsPerdidos: 0,
    setsDiff: 0,
    gamesGanados: 0,
    gamesPerdidos: 0,
    gamesDiff: 0,
    puntos: 0
  }));

  const find = (id: number) => tabla.find((t: any) => t.equipo.id === id);

  zona.partidos.forEach((p: any) => {
    if (!p.sets || p.sets.length === 0) return;
    
    const minimosRequeridos = deporte === 'Futbol' ? 1 : 2;
    if (p.sets.length < minimosRequeridos) return;
    
    const todosLosSetsTienenResultado = p.sets.every(
      (set: any) => 
        set.gamesLocal !== null && 
        set.gamesLocal !== undefined &&
        set.gamesVisitante !== null && 
        set.gamesVisitante !== undefined
    );
    
    if (!todosLosSetsTienenResultado) return;

    const l = find(p.local.id);
    const v = find(p.visitante.id);
    
    if (!l || !v) return;

    // Usar función centralizada
    const statsLocal = calcularEstadisticas([p], p.local.id, deporte);
    const statsVisitante = calcularEstadisticas([p], p.visitante.id, deporte);

    // Acumular estadísticas
    l.pj += statsLocal.pj;
    l.pg += statsLocal.pg;
    l.pe += statsLocal.pe;
    l.pp += statsLocal.pp;
    l.setsGanados += statsLocal.setsGanados;
    l.setsPerdidos += statsLocal.setsPerdidos;
    l.gamesGanados += statsLocal.gamesGanados;
    l.gamesPerdidos += statsLocal.gamesPerdidos;
    l.puntos += statsLocal.puntos;
    
    v.pj += statsVisitante.pj;
    v.pg += statsVisitante.pg;
    v.pe += statsVisitante.pe;
    v.pp += statsVisitante.pp;
    v.setsGanados += statsVisitante.setsGanados;
    v.setsPerdidos += statsVisitante.setsPerdidos;
    v.gamesGanados += statsVisitante.gamesGanados;
    v.gamesPerdidos += statsVisitante.gamesPerdidos;
    v.puntos += statsVisitante.puntos;

    // Calcular diferencias
    l.setsDiff = l.setsGanados - l.setsPerdidos;
    l.gamesDiff = l.gamesGanados - l.gamesPerdidos;
    v.setsDiff = v.setsGanados - v.setsPerdidos;
    v.gamesDiff = v.gamesGanados - v.gamesPerdidos;
  });

  // Ordenar según deporte
  return tabla.sort((a: any, b: any) => {
    if (b.puntos !== a.puntos) return b.puntos - a.puntos;
    
    if (deporte === 'Futbol') {
      if (b.gamesDiff !== a.gamesDiff) return b.gamesDiff - a.gamesDiff;
      if (b.gamesGanados !== a.gamesGanados) return b.gamesGanados - a.gamesGanados;
    } else {
      if (b.setsDiff !== a.setsDiff) return b.setsDiff - a.setsDiff;
      if (b.setsGanados !== a.setsGanados) return b.setsGanados - a.setsGanados;
      if (b.gamesDiff !== a.gamesDiff) return b.gamesDiff - a.gamesDiff;
      if (b.gamesGanados !== a.gamesGanados) return b.gamesGanados - a.gamesGanados;
    }
    
    return a.equipo.nombre.localeCompare(b.equipo.nombre);
  });
}

// ---------------------------------------------------------
// Distribución de zonas según reglamento APA
// Zonas de 3, con zonas de 4 solo para sobrantes (últimas zonas)
// ---------------------------------------------------------
function distribuirZonasAPA(totalParticipantes: number): number[] {
  const resto = totalParticipantes % 3;
  if (resto === 0) {
    // Divisible por 3 → todas zonas de 3
    return Array(totalParticipantes / 3).fill(3);
  } else if (resto === 1) {
    // Sobra 1 → una zona de 4 al final
    const zonasde3 = (totalParticipantes - 4) / 3;
    return [...Array(zonasde3).fill(3), 4];
  } else {
    // Sobran 2 → dos zonas de 4 al final
    const zonasde3 = (totalParticipantes - 8) / 3;
    return [...Array(Math.max(0, zonasde3)).fill(3), 4, 4];
  }
}

// ---------------------------------------------------------
// Generador de Zonas + Eliminación
// ---------------------------------------------------------
export function generarZonasYEliminacion(
  totalParticipantes: number,
  totalClasificadosDeseados: number,
  zonasDeseadas?: number,
  estrategiaByes: EstrategiaByes = "orden_zonas",
  deporte: Deporte = 'Tenis',
  modoZonas: ModoZonas = 'flexible'
): ResultadoTorneo | null {

  // ── MODO APA ──────────────────────────────────────────────
  if (modoZonas === 'apa') {
    const participantesPorZona = distribuirZonasAPA(totalParticipantes);
    const zonas = participantesPorZona.length;

    // Clasificados: 2 por zona de 3, 2 por zona de 4 (1º y 2º)
    const clasificadosPorZona = participantesPorZona.map(() => 2);
    const totalClasificados = clasificadosPorZona.reduce((a, b) => a + b, 0);

    const cuadroFinal = nextPowerOf2(totalClasificados);
    const byes = cuadroFinal - totalClasificados;
    const fases = Math.log2(cuadroFinal);

    const primeraRonda = generarCrucesConByes(
      clasificadosPorZona,
      totalClasificados,
      estrategiaByes,
      participantesPorZona,
      'apa'
    );

    const rondas: Ronda[] = [{ numero: 1, partidos: primeraRonda.enfrentamientos }];
    let rondaActual = primeraRonda.enfrentamientos;
    let idPartido = primeraRonda.enfrentamientos.length + 1;

    while (rondaActual.length > 1) {
      rondaActual = avanzarRondaConIds(rondaActual, idPartido);
      rondas.push({ numero: rondas.length + 1, partidos: rondaActual });
      idPartido += rondaActual.length;
    }

    return {
      formato: "zonas_y_eliminacion",
      total: totalParticipantes,
      zonas,
      participantesPorZona,
      clasificadosPorZona,
      totalClasificados,
      cuadroFinal,
      byes,
      fases,
      criterioClasificacion: generarDescripcionClasificacionAvanzado(clasificadosPorZona, participantesPorZona, 'orden_zonas'),
      rondas,
      estrategiaByes,
      modoZonas: 'apa',
      deporte
    };
  }
  // ── FIN MODO APA ──────────────────────────────────────────

  // Validaciones solo para modo flexible
  if (totalClasificadosDeseados < 1) throw new Error("totalClasificadosDeseados debe ser >= 1");
  if (totalClasificadosDeseados > totalParticipantes)
    throw new Error("No puede haber más clasificados que participantes.");

  let zonaMin, zonaMax;
  
  if (zonasDeseadas) {
    zonaMin = zonasDeseadas;
    zonaMax = zonasDeseadas;
    
    if (zonasDeseadas > totalParticipantes) {
      throw new Error(`No se pueden crear ${zonasDeseadas} zonas con solo ${totalParticipantes} equipos`);
    }
  } else {
    zonaMin = 2;
    zonaMax = Math.min(8, Math.floor(totalParticipantes / 2));
  }

  let mejorSolucion: ResultadoTorneo | null = null;
  let mejorScore = Infinity;

  for (let zonas = zonaMin; zonas <= zonaMax; zonas++) {

    const base = Math.floor(totalParticipantes / zonas);
    const extras = totalParticipantes % zonas;
    const participantesPorZona = Array(zonas)
      .fill(base)
      .map((v, i) => (i < extras ? v + 1 : v));

    if (participantesPorZona.some(p => p < 2)) {
      continue;
    }

    const clasificadosPorZona = Array(zonas).fill(1);
    let suma = clasificadosPorZona.reduce((a, b) => a + b, 0);
    let cuposRestantes = totalClasificadosDeseados - suma;

    if (cuposRestantes > 0) {
      const idxs = participantesPorZona
        .map((p, i) => ({ p, i }))
        .sort((a, b) => b.p - a.p)
        .map(x => x.i);

      let nivel = 2;
      while (cuposRestantes > 0) {
        for (const i of idxs) {
          if (clasificadosPorZona[i] < participantesPorZona[i]) {
            clasificadosPorZona[i]++;
            cuposRestantes--;
            if (cuposRestantes === 0) break;
          }
        }
        nivel++;
        if (nivel > 6) break;
      }
    }

    suma = clasificadosPorZona.reduce((a, b) => a + b, 0);
    if (suma !== totalClasificadosDeseados) continue;

    const cuadroFinal = nextPowerOf2(totalClasificadosDeseados);
    const byes = cuadroFinal - totalClasificadosDeseados;
    const fases = Math.log2(cuadroFinal);

    const avgParticipantes = totalParticipantes / zonas;
    const desviacion = Math.sqrt(
      participantesPorZona.reduce((sum, p) => sum + Math.pow(p - avgParticipantes, 2), 0) / zonas
    );
    
    const score = desviacion;

    const primeraRonda = generarCrucesConByes(
      clasificadosPorZona, 
      totalClasificadosDeseados,
      estrategiaByes,
      participantesPorZona,
      'flexible'
    );

    const rondas: Ronda[] = [ { numero: 1, partidos: primeraRonda.enfrentamientos } ];
    let rondaActual = primeraRonda.enfrentamientos;
    let idPartido = primeraRonda.enfrentamientos.length + 1;

    while (rondaActual.length > 1) {
      rondaActual = avanzarRondaConIds(rondaActual, idPartido);
      rondas.push({ numero: rondas.length + 1, partidos: rondaActual });
      idPartido += rondaActual.length;
    }

    const solucion: ResultadoTorneo = {
      formato: "zonas_y_eliminacion",
      total: totalParticipantes,
      zonas,
      participantesPorZona,
      clasificadosPorZona,
      totalClasificados: totalClasificadosDeseados,
      cuadroFinal,
      byes,
      fases,
      criterioClasificacion: generarDescripcionClasificacionAvanzado(clasificadosPorZona, participantesPorZona, estrategiaByes),
      rondas,
      estrategiaByes,
      modoZonas: 'flexible',
      zonasDeseadas: zonasDeseadas ? zonas : undefined,
      deporte
    };

    if (score < mejorScore) {
      mejorScore = score;
      mejorSolucion = solucion;
    }
  }

  return mejorSolucion;
}

// ---------------------------------------------------------
// Descripción de clasificación
// ---------------------------------------------------------
function generarDescripcionClasificacionAvanzado(
  clasificadosPorZona: number[],
  participantesPorZona?: number[],
  estrategiaByes: EstrategiaByes = 'orden_zonas'
): string {
  const minClas = Math.min(...clasificadosPorZona);
  const maxClas = Math.max(...clasificadosPorZona);

  if (minClas === maxClas) {
    return `Los ${minClas} primeros de cada zona`;
  }

  const nivelExtra = minClas + 1;
  const partes: string[] = [`Los ${minClas} primeros de cada zona`];

  if (estrategiaByes === 'mejor_clasificacion') {
    // En mejor clasificación, los cupos extra son comparativos globales
    const idxsConExtra = clasificadosPorZona.filter(c => c >= nivelExtra).length;
    for (let k = 0; k < idxsConExtra; k++) {
      partes.push(`el ${k + 1}º mejor ${nivelExtra}º`);
    }
  } else {
    // En orden_zonas, las zonas más grandes clasifican directo
    const tamañoPorIdx = participantesPorZona ?? clasificadosPorZona;
    const idxsConExtra = clasificadosPorZona
      .map((c, i) => ({ c, i }))
      .filter(x => x.c >= nivelExtra);

    const maxTamaño = Math.max(...idxsConExtra.map(x => tamañoPorIdx[x.i]));
    const directas = idxsConExtra.filter(x => tamañoPorIdx[x.i] === maxTamaño);
    const comparativas = idxsConExtra.filter(x => tamañoPorIdx[x.i] < maxTamaño);

    for (const d of directas) {
      partes.push(`el ${nivelExtra}º de zona ${numeroZonaALetra(d.i + 1)}`);
    }
    for (let k = 0; k < comparativas.length; k++) {
      partes.push(`el ${k + 1}º mejor ${nivelExtra}º de las zonas restantes`);
    }
  }

  return partes.join(', ');
}

function numeroZonaALetra(n: number): string {
  return String.fromCharCode(64 + n);
}

// Extrae el identificador de zona de un string de clasificado
// "1º de zona 3" → "3", "1º Zona C" → "C", "Mejor 2º" → null
function extraerZona(id: string): string | null {
  const m1 = id.match(/zona (\w+)$/i);
  if (m1) return m1[1].toUpperCase();
  return null;
}

// ---------------------------------------------------------
// Draws fijos APA — tablas hardcodeadas del reglamento (páginas 7-12)
// Cada entrada es [local, visitante] donde los valores son:
//   "1A" = 1º Zona A, "2B" = 2º Zona B, null = BYE
// ---------------------------------------------------------
type SlotAPA = string | null; // "1A", "2B", null

function slotAPA(nivel: number, zona: string): SlotAPA {
  return `${nivel}º Zona ${zona}`;
}
const B = null; // BYE

// Tablas de cruces por cantidad de zonas (clasifican 2 por zona siempre en APA)
// Formato: array de pares [local, visitante] en orden de partidos
const DRAWS_APA: Record<number, [SlotAPA, SlotAPA][]> = {
  // 2 zonas → 4 clasificados, 0 byes, cuadro 4
  2: [
    [slotAPA(1,'A'), slotAPA(2,'B')],
    [slotAPA(2,'A'), slotAPA(1,'B')],
  ],
  // 3 zonas → 6 clasificados, 2 byes, cuadro 8
  3: [
    [slotAPA(1,'A'), B],
    [slotAPA(2,'B'), slotAPA(2,'C')],
    [slotAPA(1,'C'), slotAPA(2,'A')],
    [slotAPA(1,'B'), B],
  ],
  // 4 zonas → 8 clasificados, 0 byes, cuadro 8
  4: [
    [slotAPA(1,'A'), slotAPA(2,'C')],
    [slotAPA(2,'B'), slotAPA(1,'D')],
    [slotAPA(1,'C'), slotAPA(2,'A')],
    [slotAPA(2,'D'), slotAPA(1,'B')],
  ],
  // 5 zonas → 10 clasificados, 6 byes, cuadro 16
  5: [
    [slotAPA(1,'A'), B],
    [slotAPA(2,'B'), slotAPA(2,'C')],
    [slotAPA(1,'E'), B],
    [slotAPA(1,'D'), B],
    [slotAPA(1,'C'), B],
    [slotAPA(2,'E'), B],
    [slotAPA(2,'A'), slotAPA(2,'D')],
    [slotAPA(1,'B'), B],
  ],
  // 6 zonas → 12 clasificados, 4 byes, cuadro 16
  6: [
    [slotAPA(1,'A'), B],
    [slotAPA(2,'F'), slotAPA(2,'C')],
    [slotAPA(1,'E'), slotAPA(2,'B')],
    [slotAPA(1,'D'), B],
    [slotAPA(1,'C'), B],
    [slotAPA(2,'A'), slotAPA(1,'F')],
    [slotAPA(2,'E'), slotAPA(2,'D')],
    [slotAPA(1,'B'), B],
  ],
  // 7 zonas → 14 clasificados, 2 byes, cuadro 16
  7: [
    [slotAPA(1,'A'), B],
    [slotAPA(2,'F'), slotAPA(2,'G')],
    [slotAPA(1,'E'), slotAPA(2,'C')],
    [slotAPA(2,'B'), slotAPA(1,'D')],
    [slotAPA(1,'C'), slotAPA(2,'A')],
    [slotAPA(2,'D'), slotAPA(1,'F')],
    [slotAPA(1,'G'), slotAPA(2,'E')],
    [slotAPA(1,'B'), B],
  ],
  // 8 zonas → 16 clasificados, 0 byes, cuadro 16
  8: [
    [slotAPA(1,'A'), slotAPA(2,'B')],
    [slotAPA(2,'G'), slotAPA(1,'H')],
    [slotAPA(1,'E'), slotAPA(2,'F')],
    [slotAPA(2,'C'), slotAPA(1,'D')],
    [slotAPA(1,'C'), slotAPA(2,'D')],
    [slotAPA(2,'E'), slotAPA(1,'F')],
    [slotAPA(1,'G'), slotAPA(2,'H')],
    [slotAPA(2,'A'), slotAPA(1,'B')],
  ],
  // 9 zonas → 18 clasificados, 14 byes, cuadro 32
  9: [
    [slotAPA(1,'A'), B],
    [slotAPA(2,'B'), slotAPA(2,'C')],
    [slotAPA(1,'I'), B],
    [slotAPA(1,'H'), B],
    [slotAPA(1,'E'), B],
    [slotAPA(2,'G'), B],
    [slotAPA(2,'F'), B],
    [slotAPA(1,'D'), B],
    [slotAPA(1,'C'), B],
    [slotAPA(2,'E'), B],
    [slotAPA(2,'H'), B],
    [slotAPA(1,'F'), B],
    [slotAPA(1,'G'), B],
    [slotAPA(2,'I'), B],
    [slotAPA(2,'D'), slotAPA(2,'A')],
    [slotAPA(1,'B'), B],
  ],
  // 10 zonas → 20 clasificados, 12 byes, cuadro 32
  10: [
    [slotAPA(1,'A'), B],
    [slotAPA(2,'C'), slotAPA(2,'F')],
    [slotAPA(1,'I'), B],
    [slotAPA(1,'H'), B],
    [slotAPA(1,'E'), B],
    [slotAPA(2,'J'), B],
    [slotAPA(2,'G'), slotAPA(2,'B')],
    [slotAPA(1,'D'), B],
    [slotAPA(1,'C'), B],
    [slotAPA(2,'A'), slotAPA(2,'H')],
    [slotAPA(2,'I'), B],
    [slotAPA(1,'F'), B],
    [slotAPA(1,'G'), B],
    [slotAPA(1,'D'), B],
    [slotAPA(2,'E'), slotAPA(2,'D')],
    [slotAPA(1,'B'), B],
  ],
};

function generarDrawAPA(
  clasificadosPorZona: number[],
  totalClasificados: number
): ReturnType<typeof generarEnfrentamientosDesdeParticipantes> {
  const numZonas = clasificadosPorZona.length;
  const cuadroFinal = nextPowerOf2(totalClasificados);
  const byes = cuadroFinal - totalClasificados;

  const tabla = DRAWS_APA[numZonas];

  if (tabla) {
    // Usar tabla hardcodeada del reglamento
    const partidos: Partido[] = tabla.map(([local, visitante], i) => ({
      id: i + 1,
      ronda: 1,
      local: local ?? null,
      visitante: visitante ?? null,
      pasaAutomatico: local === null || visitante === null,
      detalles: {}
    }));

    const identificadoresOrdenados = partidos
      .flatMap(p => [p.local, p.visitante])
      .filter(Boolean) as string[];

    return { enfrentamientos: partidos, byes, identificadoresOrdenados };
  }

  // Fallback para zonas no cubiertas: algoritmo genérico sin misma zona
  const primeros: string[] = [];
  const segundos: string[] = [];
  for (let i = 0; i < numZonas; i++) {
    const letra = String.fromCharCode(65 + i);
    if (clasificadosPorZona[i] >= 1) primeros.push(`1º Zona ${letra}`);
    if (clasificadosPorZona[i] >= 2) segundos.push(`2º Zona ${letra}`);
  }

  const primerosConBye = primeros.slice(0, byes);
  const primerosSinBye = primeros.slice(byes);
  const segundosDisponibles = [...segundos];
  const cruces: [string, string][] = [];

  for (const primero of primerosSinBye) {
    const zonaP = primero.slice(-1);
    const idx = segundosDisponibles.findIndex(s => s.slice(-1) !== zonaP);
    if (idx !== -1) {
      cruces.push([primero, segundosDisponibles.splice(idx, 1)[0]]);
    }
  }
  while (segundosDisponibles.length >= 2) {
    const s1 = segundosDisponibles.shift()!;
    const idx = segundosDisponibles.findIndex(s => s.slice(-1) !== s1.slice(-1));
    cruces.push([s1, segundosDisponibles.splice(idx !== -1 ? idx : 0, 1)[0]]);
  }

  const mitadByes = Math.ceil(byes / 2);
  const partidos: Partido[] = [];
  let id = 1;
  for (const bye of primerosConBye.slice(0, mitadByes))
    partidos.push({ id: id++, ronda: 1, local: bye, visitante: null, pasaAutomatico: true, detalles: {} });
  for (const [local, visitante] of cruces)
    partidos.push({ id: id++, ronda: 1, local, visitante, pasaAutomatico: false, detalles: {} });
  for (const bye of primerosConBye.slice(mitadByes))
    partidos.push({ id: id++, ronda: 1, local: bye, visitante: null, pasaAutomatico: true, detalles: {} });

  const identificadoresOrdenados = partidos
    .flatMap(p => [p.local, p.visitante])
    .filter(Boolean) as string[];

  return { enfrentamientos: partidos, byes, identificadoresOrdenados };
}

// ---------------------------------------------------------
// Generador de cruces con BYEs
// ---------------------------------------------------------
export function generarCrucesConByes(
  clasificadosPorZona: number[], 
  totalClasificados: number,
  estrategiaByes: EstrategiaByes = "orden_zonas",
  participantesPorZona?: number[],
  modoZonas: ModoZonas = 'flexible'
) {
  const cuadroFinal = nextPowerOf2(totalClasificados);
  const byes = cuadroFinal - totalClasificados;

  // Modo APA con orden_zonas → usar tablas hardcodeadas del reglamento
  if (modoZonas === 'apa' && estrategiaByes === 'orden_zonas') {
    return generarDrawAPA(clasificadosPorZona, totalClasificados);
  }

  const clasificadosPrioridad: string[] = [];
  const maxNivel = Math.max(...clasificadosPorZona);

  // Genera los identificadores de clasificados respetando la prioridad por tamaño de zona
  function generarIdentificadores(): string[] {
    const ids: string[] = [];
    const numZonas = clasificadosPorZona.length;

    for (let nivel = 1; nivel <= maxNivel; nivel++) {
      const idxsConEsteNivel = clasificadosPorZona
        .map((c, i) => ({ c, i }))
        .filter(x => x.c >= nivel)
        .map(x => x.i);

      const zonasConEsteNivel = idxsConEsteNivel.length;

      if (zonasConEsteNivel === numZonas) {
        // Todos tienen este nivel → clasificación directa por zona
        clasificadosPorZona.forEach((c, i) => {
          if (c >= nivel) ids.push(`${nivel}º de zona ${i + 1}`);
        });
      } else {
        // No todas las zonas tienen este nivel.
        // Determinar cuáles son "más grandes" usando participantesPorZona si está disponible,
        // o clasificadosPorZona como proxy.
        const tamañoPorIdx = participantesPorZona ?? clasificadosPorZona;
        const maxTamaño = Math.max(...idxsConEsteNivel.map(i => tamañoPorIdx[i]));
        
        let comparativasCount = 0;
        for (const idx of idxsConEsteNivel) {
          if (tamañoPorIdx[idx] === maxTamaño) {
            // Zona más grande → clasifica directo
            ids.push(`${nivel}º de zona ${idx + 1}`);
          } else {
            // Zona de igual tamaño que las que NO tienen este nivel → comparativa
            comparativasCount++;
            ids.push(`${comparativasCount}º mejor ${nivel}º`);
          }
        }
      }
    }

    return ids;
  }

  if (estrategiaByes === "orden_zonas") {
    // Modo flexible con orden_zonas: construir partidos directamente
    // evitando que se enfrenten clasificados de la misma zona
    const ids = generarIdentificadores();

    // Separar primeros y segundos (y niveles extra)
    const primeros = ids.filter(id => id.startsWith('1º'));
    const segundos = ids.filter(id => id.startsWith('2º'));
    const extras = ids.filter(id => !id.startsWith('1º') && !id.startsWith('2º'));

    // Los primeros `byes` primeros reciben bye
    const primerosConBye = primeros.slice(0, byes);
    const primerosSinBye = primeros.slice(byes);

    // Asignar segundos a primeros sin bye, evitando misma zona
    const segundosDisponibles = [...segundos];
    const cruces: [string, string][] = [];

    for (const primero of primerosSinBye) {
      const zonaP = extraerZona(primero);
      const idx = segundosDisponibles.findIndex(s => extraerZona(s) !== zonaP);
      if (idx !== -1) {
        cruces.push([primero, segundosDisponibles.splice(idx, 1)[0]]);
      } else if (segundosDisponibles.length > 0) {
        // Último recurso: no hay opción sin misma zona
        cruces.push([primero, segundosDisponibles.shift()!]);
      }
    }

    // Segundos sobrantes se cruzan entre sí, evitando misma zona
    while (segundosDisponibles.length >= 2) {
      const s1 = segundosDisponibles.shift()!;
      const zonaS1 = extraerZona(s1);
      const idx = segundosDisponibles.findIndex(s => extraerZona(s) !== zonaS1);
      cruces.push([s1, segundosDisponibles.splice(idx !== -1 ? idx : 0, 1)[0]]);
    }

    // Extras (3º, etc.) se agregan al final como cruces entre sí
    for (let i = 0; i < extras.length - 1; i += 2) {
      cruces.push([extras[i], extras[i + 1]]);
    }

    // Armar partidos: byes arriba (mitad), cruces, byes abajo (mitad)
    const mitadByes = Math.ceil(byes / 2);
    const partidos: Partido[] = [];
    let id = 1;

    for (const bye of primerosConBye.slice(0, mitadByes))
      partidos.push({ id: id++, ronda: 1, local: bye, visitante: null, pasaAutomatico: true, detalles: {} });
    for (const [local, visitante] of cruces)
      partidos.push({ id: id++, ronda: 1, local, visitante, pasaAutomatico: false, detalles: {} });
    for (const bye of primerosConBye.slice(mitadByes))
      partidos.push({ id: id++, ronda: 1, local: bye, visitante: null, pasaAutomatico: true, detalles: {} });

    const identificadoresOrdenados = partidos
      .flatMap(p => [p.local, p.visitante])
      .filter(Boolean) as string[];

    return { enfrentamientos: partidos, byes, identificadoresOrdenados };
  }
  
  else if (estrategiaByes === "mejor_clasificacion") {
    for (let nivel = 1; nivel <= maxNivel; nivel++) {
      const clasificadosEsteNivel = clasificadosPorZona.filter(c => c >= nivel).length;
      
      for (let i = 0; i < clasificadosEsteNivel; i++) {
        const posicion = i + 1;
        if (clasificadosEsteNivel === 1) {
          clasificadosPrioridad.push(`Mejor ${nivel}º`);
        } else {
          clasificadosPrioridad.push(`${posicion}º mejor ${nivel}º`);
        }
      }
    }
    
    const participantes: (string | null)[] = [];
    
    if (byes > 0) {
      const mejoresConBye = clasificadosPrioridad.slice(0, byes);
      const restantes = clasificadosPrioridad.slice(byes);
      participantes.push(...mejoresConBye, ...restantes);
    } else {
      participantes.push(...clasificadosPrioridad);
    }
    
    return generarEnfrentamientosDesdeParticipantes(participantes, cuadroFinal, byes);
  }
  
  else {
    for (let nivel = 1; nivel <= maxNivel; nivel++) {
      clasificadosPorZona.forEach((c, i) => {
        if (c >= nivel) clasificadosPrioridad.push(`${nivel}º mejor de zona ${i + 1}`);
      });
    }
    const mejoresConBye = clasificadosPrioridad.slice(0, byes);
    const restantes = clasificadosPrioridad.slice(byes);
    const participantes = [...mejoresConBye, ...restantes];
    return generarEnfrentamientosDesdeParticipantes(participantes, cuadroFinal, byes);
  }
}

function generarEnfrentamientosDesdeParticipantes(
  participantes: (string | null)[], 
  cuadroFinal: number,
  byes: number
) {
  while (participantes.length < cuadroFinal) participantes.push(null);

  const enfrentamientos: Partido[] = [];
  for (let i = 0; i < cuadroFinal / 2; i++) {
    const local = participantes[i];
    const visitante = participantes[cuadroFinal - 1 - i];
    enfrentamientos.push({
      id: i + 1,
      ronda: 1,
      local,
      visitante,
      pasaAutomatico: local === null || visitante === null,
      detalles: {}
    });
  }

  const enfrentamientosConBye = enfrentamientos.filter(e => e.pasaAutomatico);
  const enfrentamientosSinBye = enfrentamientos.filter(e => !e.pasaAutomatico);
  
  const enfrentamientosReordenados: Partido[] = [];
  const mitadByes = Math.ceil(enfrentamientosConBye.length / 2);
  const byesPrimeraMetad = enfrentamientosConBye.slice(0, mitadByes);
  const byesSegundaMetad = enfrentamientosConBye.slice(mitadByes);
  
  const byesSerpenteados: Partido[] = [];
  for (let i = 0; i < Math.max(byesPrimeraMetad.length, byesSegundaMetad.length); i++) {
    if (i < byesPrimeraMetad.length) byesSerpenteados.push(byesPrimeraMetad[i]);
    if (i < byesSegundaMetad.length) byesSerpenteados.push(byesSegundaMetad[i]);
  }
  
  let idxByes = 0;
  let idxNormales = 0;
  
  for (let i = 0; i < enfrentamientos.length; i++) {
    if (idxByes < byesSerpenteados.length && 
        (i % 2 === 0 || idxNormales >= enfrentamientosSinBye.length)) {
      enfrentamientosReordenados.push(byesSerpenteados[idxByes]);
      idxByes++;
    } else if (idxNormales < enfrentamientosSinBye.length) {
      enfrentamientosReordenados.push(enfrentamientosSinBye[idxNormales]);
      idxNormales++;
    }
  }
  
  enfrentamientosReordenados.forEach((e, i) => e.id = i + 1);

  const identificadoresOrdenados: string[] = [];
  const participantesAntesNull = [...participantes].filter(p => p !== null) as string[];
  identificadoresOrdenados.push(...participantesAntesNull);
  
  return { 
    enfrentamientos: enfrentamientosReordenados, 
    byes,
    identificadoresOrdenados
  };
}

// ---------------------------------------------------------
// Avanzar ronda
// ---------------------------------------------------------
function avanzarRondaConIds(partidos: Partido[], idInicio: number): Partido[] {
  const ganadoresIds = partidos.map(p => p.id);
  const nuevaRonda: Partido[] = [];

  for (let i = 0; i < ganadoresIds.length; i += 2) {
    const local = ganadoresIds[i];
    const visitante = ganadoresIds[i + 1] || null;
    nuevaRonda.push({
      id: idInicio + (i / 2),
      ronda: (partidos[0]?.ronda ?? 1) + 1,
      local: null,
      visitante: null,
      pasaAutomatico: visitante === null,
      dePartido: [local, visitante!]
    });
  }

  return nuevaRonda;
}

// ---------------------------------------------------------
// Generador de eliminación directa
// ---------------------------------------------------------
export function generarEliminacionDirecta(
  total: number,
  deporte: Deporte = 'Tenis'
): ResultadoTorneo {
  const cuadroFinal = nextPowerOf2(total);
  const byes = cuadroFinal - total;
  const fases = Math.log2(cuadroFinal);

  const equiposIniciales = Array.from({ length: total }, (_, i) => ({
    id: i + 1,
    nombre: `Equipo ${i + 1}`,
  }));

  const participantes: (typeof equiposIniciales[0] | null)[] = [...equiposIniciales];
  while (participantes.length < cuadroFinal) {
    participantes.push(null);
  }

  const primeraRonda: Partido[] = [];
  for (let i = 0; i < cuadroFinal / 2; i++) {
    const local = participantes[i];
    const visitante = participantes[cuadroFinal - 1 - i];
    
    primeraRonda.push({
      id: i + 1,
      ronda: 1,
      local,
      visitante,
      pasaAutomatico: local === null || visitante === null,
      ganador: (local === null || visitante === null) 
        ? (local || visitante) 
        : undefined,
      detalles: {}
    });
  }

  const rondas: Ronda[] = [{ numero: 1, partidos: primeraRonda }];
  let rondaActual = primeraRonda;
  let idPartido = primeraRonda.length + 1;

  while (rondaActual.length > 1) {
    rondaActual = avanzarRondaConIds(rondaActual, idPartido);
    rondas.push({ numero: rondas.length + 1, partidos: rondaActual });
    idPartido += rondaActual.length;
  }

  return {
    formato: "eliminacion_directa",
    total,
    cuadroFinal,
    byes,
    fases,
    rondas,
    deporte
  };
}

// ---------------------------------------------------------
// Función principal
// ---------------------------------------------------------
export function generarTorneo(
  total: number,
  modalidad: "eliminacion_directa" | "zonas_y_eliminacion",
  clasificados?: number,
  zonasDeseadas?: number,
  estrategiaByes: EstrategiaByes = "orden_zonas",
  deporte: Deporte = 'Tenis',
  modoZonas: ModoZonas = 'flexible'
): ResultadoTorneo | null {
  if (modalidad === "eliminacion_directa") {
    return generarEliminacionDirecta(total, deporte);
  }
  return generarZonasYEliminacion(
    total, 
    clasificados!, 
    zonasDeseadas,
    estrategiaByes,
    deporte,
    modoZonas
  );
}

// ---------------------------------------------------------
// Modo Cruces — Funciones para zonas de 4 equipos
// ---------------------------------------------------------

/**
 * Genera los 2 partidos iniciales del modo cruces para una zona de 4 equipos.
 * Partido 1: equipos[0] vs equipos[1] (inicial_1)
 * Partido 2: equipos[2] vs equipos[3] (inicial_2)
 */
export function generarPartidosCruces(zona: ZonaCruces, deporte: Deporte): PartidoZona[] {
  if (zona.equipos.length !== 4) {
    console.warn(`generarPartidosCruces: la zona ${zona.id} no tiene exactamente 4 equipos`);
    return [];
  }
  const sets = () => crearSetsIniciales(deporte) as Array<{ gamesLocal: number | null; gamesVisitante: number | null }>;
  return [
    {
      id: `Z${zona.id}P1`,
      local: zona.equipos[0],
      visitante: zona.equipos[1],
      sets: sets(),
      jugado: false,
      detalles: {},
      tipoCruce: 'inicial_1',
    },
    {
      id: `Z${zona.id}P2`,
      local: zona.equipos[2],
      visitante: zona.equipos[3],
      sets: sets(),
      jugado: false,
      detalles: {},
      tipoCruce: 'inicial_2',
    },
  ];
}

/**
 * Determina el ganador y perdedor de un partido según los sets cargados.
 * Retorna null si el partido no tiene resultado completo o hay empate irresolvible.
 */
export function determinarGanadorPerdedor(
  partido: PartidoZona,
  deporte: Deporte
): { ganador: Equipo; perdedor: Equipo } | null {
  if (!partido.sets || partido.sets.length === 0) return null;
  const todosCompletos = partido.sets.every(
    s => s.gamesLocal !== null && s.gamesVisitante !== null
  );
  if (!todosCompletos) return null;

  let setsLocal = 0;
  let setsVisitante = 0;
  let gamesLocal = 0;
  let gamesVisitante = 0;

  for (const s of partido.sets) {
    const gl = s.gamesLocal!;
    const gv = s.gamesVisitante!;
    gamesLocal += gl;
    gamesVisitante += gv;
    if (gl > gv) setsLocal++;
    else if (gv > gl) setsVisitante++;
  }

  if (setsLocal > setsVisitante) return { ganador: partido.local, perdedor: partido.visitante };
  if (setsVisitante > setsLocal) return { ganador: partido.visitante, perdedor: partido.local };
  // Empate en sets → desempatar por games
  if (gamesLocal > gamesVisitante) return { ganador: partido.local, perdedor: partido.visitante };
  if (gamesVisitante > gamesLocal) return { ganador: partido.visitante, perdedor: partido.local };
  return null; // empate irresolvible
}

/**
 * Genera el partido final y el partido por el 3er puesto cuando ambos iniciales tienen resultado.
 * Es idempotente: si ya existen, no genera duplicados.
 * Retorna true si generó los partidos, false si no correspondía.
 */
export function generarPartidosFinalesCruces(zona: ZonaCruces, deporte: Deporte): boolean {
  const inicial1 = zona.partidos.find(p => p.tipoCruce === 'inicial_1');
  const inicial2 = zona.partidos.find(p => p.tipoCruce === 'inicial_2');
  if (!inicial1 || !inicial2) return false;

  const res1 = determinarGanadorPerdedor(inicial1, deporte);
  const res2 = determinarGanadorPerdedor(inicial2, deporte);
  if (!res1 || !res2) return false;

  // Idempotencia
  const yaExisteFinal = zona.partidos.some(p => p.tipoCruce === 'final');
  const yaExisteTercero = zona.partidos.some(p => p.tipoCruce === 'tercer_puesto');
  if (yaExisteFinal || yaExisteTercero) return false;

  const sets = () => crearSetsIniciales(deporte) as Array<{ gamesLocal: number | null; gamesVisitante: number | null }>;

  zona.partidos.push({
    id: `Z${zona.id}P3`,
    local: res1.ganador,
    visitante: res2.ganador,
    sets: sets(),
    jugado: false,
    detalles: {},
    tipoCruce: 'final',
  });

  zona.partidos.push({
    id: `Z${zona.id}P4`,
    local: res1.perdedor,
    visitante: res2.perdedor,
    sets: sets(),
    jugado: false,
    detalles: {},
    tipoCruce: 'tercer_puesto',
  });

  return true;
}

/**
 * Calcula la tabla de posiciones de una zona en modo cruces.
 * Retorna el mismo formato que calcularTablaZona para ser intercambiable en el template.
 */
export function calcularTablaCruces(zona: ZonaCruces, deporte: Deporte = 'Tenis'): FilaTabla[] {
  // Inicializar filas para todos los equipos
  const filas: FilaTabla[] = zona.equipos.map(eq => ({
    equipo: eq,
    pj: 0, pg: 0, pe: 0, pp: 0,
    setsGanados: 0, setsPerdidos: 0, setsDiff: 0,
    gamesGanados: 0, gamesPerdidos: 0, gamesDiff: 0,
    puntos: 0,
  }));

  const find = (id: number) => filas.find(f => f.equipo.id === id);

  // Acumular estadísticas de todos los partidos con resultado
  for (const p of zona.partidos) {
    const res = determinarGanadorPerdedor(p, deporte);
    if (!res) continue;
    const fLocal = find(p.local.id);
    const fVisitante = find(p.visitante.id);
    if (!fLocal || !fVisitante) continue;

    fLocal.pj++; fVisitante.pj++;
    if (res.ganador.id === p.local.id) {
      fLocal.pg++; fVisitante.pp++;
      fLocal.puntos += 3;
    } else {
      fVisitante.pg++; fLocal.pp++;
      fVisitante.puntos += 3;
    }
    for (const s of p.sets) {
      if (s.gamesLocal === null || s.gamesVisitante === null) continue;
      fLocal.gamesGanados += s.gamesLocal;
      fLocal.gamesPerdidos += s.gamesVisitante;
      fVisitante.gamesGanados += s.gamesVisitante;
      fVisitante.gamesPerdidos += s.gamesLocal;
      if (s.gamesLocal > s.gamesVisitante) { fLocal.setsGanados++; fVisitante.setsPerdidos++; }
      else if (s.gamesVisitante > s.gamesLocal) { fVisitante.setsGanados++; fLocal.setsPerdidos++; }
    }
    fLocal.setsDiff = fLocal.setsGanados - fLocal.setsPerdidos;
    fLocal.gamesDiff = fLocal.gamesGanados - fLocal.gamesPerdidos;
    fVisitante.setsDiff = fVisitante.setsGanados - fVisitante.setsPerdidos;
    fVisitante.gamesDiff = fVisitante.gamesGanados - fVisitante.gamesPerdidos;
  }

  // Determinar posiciones según resultados del bracket
  const final = zona.partidos.find(p => p.tipoCruce === 'final');
  const tercero = zona.partidos.find(p => p.tipoCruce === 'tercer_puesto');
  const inicial1 = zona.partidos.find(p => p.tipoCruce === 'inicial_1');
  const inicial2 = zona.partidos.find(p => p.tipoCruce === 'inicial_2');

  const resFinal = final ? determinarGanadorPerdedor(final, deporte) : null;
  const resTercero = tercero ? determinarGanadorPerdedor(tercero, deporte) : null;
  const resInicial1 = inicial1 ? determinarGanadorPerdedor(inicial1, deporte) : null;
  const resInicial2 = inicial2 ? determinarGanadorPerdedor(inicial2, deporte) : null;

  if (resFinal && resTercero) {
    // Bracket completo
    const setPos = (id: number, pos: number) => { const f = find(id); if (f) f.posicionCruce = pos; };
    setPos(resFinal.ganador.id, 1);
    setPos(resFinal.perdedor.id, 2);
    setPos(resTercero.ganador.id, 3);
    setPos(resTercero.perdedor.id, 4);
    return filas.sort((a, b) => (a.posicionCruce ?? 99) - (b.posicionCruce ?? 99));
  }

  if (resInicial1 && resInicial2) {
    // Solo iniciales completos
    const setPos = (id: number, pos: number) => { const f = find(id); if (f) f.posicionCruce = pos; };
    setPos(resInicial1.ganador.id, 1);
    setPos(resInicial2.ganador.id, 2);
    setPos(resInicial1.perdedor.id, 3);
    setPos(resInicial2.perdedor.id, 4);
    return filas.sort((a, b) => (a.posicionCruce ?? 99) - (b.posicionCruce ?? 99));
  }

  // Sin resultados — orden original
  return filas;
}
